import { inject, Injectable } from '@angular/core';
import { PocketbaseService } from './pocketbase.service';
import { AuthService } from './auth.service';
import { CustomToastrService } from './toastr.service';

export interface FeedbackSubmission {
  owner: string; // Relation to users collection
  message: string;
  type: 'bug' | 'feature' | 'general' | 'improvement';
  page_url?: string;
  user_agent?: string;
  created?: string;
}

export interface NPSResponse {
  owner: string; // Relation to users collection
  request: string; // Relation to requests collection
  case: string; // Relation to cases collection
  rating: number; // 1-5 stars
  comment?: string;
  created?: string;
}

export interface AnnotationFeedback {
  owner: string; // Relation to users collection
  request: string; // Relation to requests collection
  case: string; // Relation to cases collection
  annotation_id: string; // Unique identifier for the annotation (e.g., "tab3_answer0_chunk5")
  feedback_type: 'thumbs_up' | 'thumbs_down';
  comment?: string; // Optional comment
  created?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {
  private readonly pocketbaseService = inject(PocketbaseService);
  private readonly authService = inject(AuthService);
  private readonly toastr = inject(CustomToastrService);

  private readonly pb = this.pocketbaseService.getPocketBase();

  /**
   * Submit general feedback to PocketBase
   */
  async submitFeedback(feedback: Omit<FeedbackSubmission, 'owner' | 'created'>): Promise<boolean> {
    try {
      const user = this.authService.user();

      if (!user?.id) {
        this.toastr.showError('Du musst angemeldet sein, um Feedback zu senden.');
        return false;
      }

      const feedbackData: FeedbackSubmission = {
        owner: user.id,
        message: feedback.message,
        type: feedback.type,
        page_url: feedback.page_url || window.location.href,
        user_agent: feedback.user_agent || navigator.userAgent
      };

      await this.pb.collection('feedback').create(feedbackData);

      this.toastr.showSuccess('Vielen Dank für dein Feedback!');
      return true;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      this.toastr.showError('Feedback konnte nicht gesendet werden. Bitte versuche es später erneut.');
      return false;
    }
  }

  /**
   * Submit NPS rating after case completion
   */
  async submitNPSRating(npsData: Omit<NPSResponse, 'owner' | 'created'>): Promise<boolean> {
    try {
      const user = this.authService.user();

      if (!user?.id) {
        console.error('Cannot submit NPS rating: user not authenticated');
        return false;
      }

      const ratingData: NPSResponse = {
        owner: user.id,
        request: npsData.request,
        case: npsData.case,
        rating: npsData.rating,
        comment: npsData.comment
      };

      await this.pb.collection('nps_ratings').create(ratingData);

      return true;
    } catch (error) {
      console.error('Error submitting NPS rating:', error);
      return false;
    }
  }

  /**
   * Check if user has already rated a specific request
   */
  async hasUserRatedRequest(requestId: string): Promise<boolean> {
    try {
      const user = this.authService.user();
      if (!user) return false;

      const records = await this.pb.collection('nps_ratings').getList(1, 1, {
        filter: `owner = "${user.id}" && request = "${requestId}"`
      });

      return records.totalItems > 0;
    } catch (error) {
      console.error('Error checking NPS rating:', error);
      return false;
    }
  }

  /**
   * Get user's feedback history
   */
  async getUserFeedback(limit: number = 10): Promise<FeedbackSubmission[]> {
    try {
      const user = this.authService.user();
      if (!user) return [];

      const records = await this.pb.collection('feedback').getList(1, limit, {
        filter: `owner = "${user.id}"`,
        sort: '-created',
        expand: 'owner' // Expand the owner relation to get user details
      });

      return records.items as unknown as FeedbackSubmission[];
    } catch (error) {
      console.error('Error fetching user feedback:', error);
      return [];
    }
  }

  /**
   * Submit annotation feedback (thumbs up/down)
   */
  async submitAnnotationFeedback(feedbackData: Omit<AnnotationFeedback, 'owner' | 'created'>): Promise<boolean> {
    try {
      const user = this.authService.user();

      if (!user?.id) {
        console.error('Cannot submit annotation feedback: user not authenticated');
        return false;
      }

      // Check if user has already given feedback for this annotation
      const existingFeedback = await this.pb.collection('annotation_feedback').getList(1, 1, {
        filter: `owner = "${user.id}" && annotation_id = "${feedbackData.annotation_id}"`
      });

      const annotationFeedbackData: any = {
        owner: user.id,
        request: feedbackData.request,
        case: feedbackData.case,
        annotation_id: feedbackData.annotation_id,
        feedback_type: feedbackData.feedback_type
      };

      // Only include comment if it's not empty
      if (feedbackData.comment && feedbackData.comment.trim().length > 0) {
        annotationFeedbackData.comment = feedbackData.comment.trim();
      }

      if (existingFeedback.totalItems > 0) {
        // Update existing feedback
        await this.pb.collection('annotation_feedback').update(existingFeedback.items[0].id, annotationFeedbackData);
      } else {
        // Create new feedback
        await this.pb.collection('annotation_feedback').create(annotationFeedbackData);
      }

      return true;
    } catch (error) {
      console.error('Error submitting annotation feedback:', error);
      return false;
    }
  }

  /**
   * Get user's feedback for a specific annotation
   */
  async getAnnotationFeedback(annotationId: string): Promise<AnnotationFeedback | null> {
    try {
      const user = this.authService.user();
      if (!user) return null;

      const records = await this.pb.collection('annotation_feedback').getList(1, 1, {
        filter: `owner = "${user.id}" && annotation_id = "${annotationId}"`,
        requestKey: null // Allow multiple parallel requests
      });

      return records.totalItems > 0 ? records.items[0] as unknown as AnnotationFeedback : null;
    } catch (error) {
      console.error('Error fetching annotation feedback:', error);
      return null;
    }
  }
}

